import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NormalFormSelectionField, NormalFormTextField } from '../molecules';
import { Button } from '../atoms';
import {
  IncidentCategoryOption,
  IncidentFilterInput,
  IncidentStatusOption,
  IncidentTypeOption,
  OrderDirectionOption,
  PageSizeOption,
  PriorityOption,
  useGetAllCitiesLazyQuery,
  useGetAllStatesLazyQuery,
  useGetCountryIsoCodesQuery,
} from '@/graphql/generated';
import { useUserLanguage } from '@/customHooks';
import {
  incidentCategoryMapper,
  incidentStatusOptionMapper,
  incidentTypeMapper,
  orderDirectionMapper,
  pageSizeMapper,
  priorityMapper
} from '@/helpers';

type IncidentFiltersProps = {
  filters: IncidentFilterInput;
  onFilterChange: (newFilters: IncidentFilterInput) => void;
};

const textsData = {
  en: {
    header: 'Filters',
    field1: 'Priority',
    field2: 'Only Status',
    field3: 'Exclude Status',
    field4: 'Type',
    field5: 'Category',
    field6: 'Order',
    field7: 'Page Size',
    search: 'Search',
    searchPlaceholder: 'Search incidents...',
  },
  es: {
    header: 'Filtros',
    field1: 'Prioridad',
    field2: 'Sólo Estado.',
    field3: 'Excluir Estado.',
    field4: 'Tipo',
    field5: 'Categoría',
    field6: 'Orden',
    field7: 'Registros por página',
    search: 'Buscar',
    searchPlaceholder: 'Buscar incidentes...',
  },
}

const initialFilters: IncidentFilterInput = {
  includeStatusOption: null,
  excludeStatusOption: null,
  includeStatusOptions: null,
  excludeStatusOptions: null,
  countryId: null,
  countryIds: null,
  stateId: null,
  stateIds: null,
  cityId: null,
  cityIds: null,
  typeOption: null,
  categoryOption: null,
  priorityOption: null,
  orderDirection: OrderDirectionOption.Desc,
  pageSize: PageSizeOption.TwentyFive,
  search: null,
  page: null,
};

export const IncidentFilters = ({ filters, onFilterChange }: IncidentFiltersProps) => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en
  const [isCollapsed, setIsCollapsed] = useState(false);

  const { data: countriesData } = useGetCountryIsoCodesQuery();
  const [getStates, { data: statesData }] = useGetAllStatesLazyQuery();
  const [getCities] = useGetAllCitiesLazyQuery();

  const [isIncludeOpen, setIsIncludeOpen] = useState(false);
  const [isExcludeOpen, setIsExcludeOpen] = useState(false);
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [isStateOpen, setIsStateOpen] = useState(false);
  const includeRef = useRef<HTMLDivElement>(null);
  const excludeRef = useRef<HTMLDivElement>(null);
  const countryRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (includeRef.current && !includeRef.current.contains(target)) setIsIncludeOpen(false);
      if (excludeRef.current && !excludeRef.current.contains(target)) setIsExcludeOpen(false);
      if (countryRef.current && !countryRef.current.contains(target)) setIsCountryOpen(false);
      if (stateRef.current && !stateRef.current.contains(target)) setIsStateOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsIncludeOpen(false);
        setIsExcludeOpen(false);
        setIsCountryOpen(false);
        setIsStateOpen(false);
      }
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const handleFieldChange = useCallback((field: keyof IncidentFilterInput, value: string | boolean | null) => {
    const newFilters: IncidentFilterInput = {
      ...filters,
      [field]: value,
    };
    onFilterChange(newFilters);
  }, [filters, onFilterChange]);

  const statusOptions = Object.values(IncidentStatusOption).map(value => ({
    name: incidentStatusOptionMapper(value, userLanguage),
    value: value,
  }));

  const selectedIncludeStatuses =
    (filters.includeStatusOptions?.length
      ? filters.includeStatusOptions
      : (filters.includeStatusOption ? [filters.includeStatusOption] : [])) ?? [];

  const selectedExcludeStatuses =
    (filters.excludeStatusOptions?.length
      ? filters.excludeStatusOptions
      : (filters.excludeStatusOption ? [filters.excludeStatusOption] : [])) ?? [];

  const toggleStatusInArray = (arr: IncidentStatusOption[], value: IncidentStatusOption) => {
    return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
  };

  const handleIncludeStatusToggle = useCallback((value: IncidentStatusOption) => {
    const next = toggleStatusInArray(selectedIncludeStatuses, value);
    const newFilters: IncidentFilterInput = {
      ...filters,
      includeStatusOptions: next.length ? next : null,
      includeStatusOption: null,
      // Evitamos filtros contradictorios: o incluyo o excluyo
      excludeStatusOptions: null,
      excludeStatusOption: null,
    };
    onFilterChange(newFilters);
  }, [filters, onFilterChange, selectedIncludeStatuses]);

  const handleExcludeStatusToggle = useCallback((value: IncidentStatusOption) => {
    const next = toggleStatusInArray(selectedExcludeStatuses, value);
    const newFilters: IncidentFilterInput = {
      ...filters,
      excludeStatusOptions: next.length ? next : null,
      excludeStatusOption: null,
      // Evitamos filtros contradictorios: o excluyo o incluyo
      includeStatusOptions: null,
      includeStatusOption: null,
    };
    onFilterChange(newFilters);
  }, [filters, onFilterChange, selectedExcludeStatuses]);

  const countryOptions = useMemo(() => {
    return (countriesData?.countries ?? [])
      .map((c) => ({ name: c.name, value: c.iso2 }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [countriesData?.countries]);

  const selectedCountryIds =
    (filters.countryIds?.length
      ? filters.countryIds
      : (filters.countryId ? [filters.countryId] : [])) ?? [];

  const activeCountryId = selectedCountryIds.length === 1 ? selectedCountryIds[0] : null;

  useEffect(() => {
    if (activeCountryId) {
      getStates({ variables: { countryCode: activeCountryId } });
    }
  }, [activeCountryId, getStates]);

  const toggleStringInArray = (arr: string[], value: string) => {
    return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
  };

  const handleCountryToggle = useCallback((countryIso2: string) => {
    const next = toggleStringInArray(selectedCountryIds, countryIso2);

    const newFilters: IncidentFilterInput = {
      ...filters,
      countryIds: next.length ? next : null,
      countryId: null,
      stateIds: null,
      stateId: null,
      cityIds: null,
      cityId: null,
    };

    onFilterChange(newFilters);
  }, [filters, onFilterChange, selectedCountryIds]);

  const countrySummary = useMemo(() => {
    if (!selectedCountryIds.length) return userLanguage === 'es' ? 'Todos' : 'All';
    if (selectedCountryIds.length === 1) {
      const only = countryOptions.find((o) => o.value === selectedCountryIds[0]);
      return only?.name ?? selectedCountryIds[0];
    }
    return userLanguage === 'es'
      ? `${selectedCountryIds.length} seleccionados`
      : `${selectedCountryIds.length} selected`;
  }, [countryOptions, selectedCountryIds, userLanguage]);

  const stateOptions = useMemo(() => {
    return (statesData?.states ?? [])
      .map((s) => ({ name: s.name, value: s.geonamesAdmin1Code }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [statesData?.states]);

  const selectedStateIds =
    (filters.stateIds?.length
      ? filters.stateIds
      : (filters.stateId ? [filters.stateId] : [])) ?? [];

  const activeStateId = selectedStateIds.length === 1 ? selectedStateIds[0] : null;

  useEffect(() => {
    if (activeStateId) {
      getCities({ variables: { stateCode: activeStateId } });
    }
  }, [activeStateId, getCities]);

  const handleStateToggle = useCallback((stateCode: string) => {
    const next = toggleStringInArray(selectedStateIds, stateCode);
    const newFilters: IncidentFilterInput = {
      ...filters,
      stateIds: next.length ? next : null,
      stateId: null,
      cityIds: null,
      cityId: null,
    };
    onFilterChange(newFilters);
  }, [filters, onFilterChange, selectedStateIds]);

  const stateSummary = useMemo(() => {
    if (!selectedStateIds.length) return userLanguage === 'es' ? 'Todos' : 'All';
    if (selectedStateIds.length === 1) {
      const only = stateOptions.find((o) => o.value === selectedStateIds[0]);
      return only?.name ?? selectedStateIds[0];
    }
    return userLanguage === 'es'
      ? `${selectedStateIds.length} seleccionados`
      : `${selectedStateIds.length} selected`;
  }, [selectedStateIds, stateOptions, userLanguage]);

  const includeSummary = useMemo(() => {
    if (!selectedIncludeStatuses.length) return userLanguage === 'es' ? 'Todos' : 'All';
    if (selectedIncludeStatuses.length === 1) {
      const only = selectedIncludeStatuses[0];
      return incidentStatusOptionMapper(only, userLanguage);
    }
    return userLanguage === 'es'
      ? `${selectedIncludeStatuses.length} seleccionados`
      : `${selectedIncludeStatuses.length} selected`;
  }, [selectedIncludeStatuses, userLanguage]);

  const excludeSummary = useMemo(() => {
    if (!selectedExcludeStatuses.length) return userLanguage === 'es' ? 'Ninguno' : 'None';
    if (selectedExcludeStatuses.length === 1) {
      const only = selectedExcludeStatuses[0];
      return incidentStatusOptionMapper(only, userLanguage);
    }
    return userLanguage === 'es'
      ? `${selectedExcludeStatuses.length} seleccionados`
      : `${selectedExcludeStatuses.length} selected`;
  }, [selectedExcludeStatuses, userLanguage]);

  const priorityOption = Object.values(PriorityOption).map(value => ({
    name: priorityMapper(value, userLanguage),
    value: value
  }))

  const typeOptions = Object.values(IncidentTypeOption).map(value => ({
    name: incidentTypeMapper(value, userLanguage),
    value: value,
  }));

  const categoryOptions = Object.values(IncidentCategoryOption).map(value => ({
    name: incidentCategoryMapper(value, userLanguage),
    value: value,
  }));

  const orderDirectionOptions = Object.values(OrderDirectionOption).map(value => ({
    name: orderDirectionMapper(value, userLanguage),
    value: value,
  }));

  const pageSizeOptions = [
    PageSizeOption.TwentyFive,
    PageSizeOption.Fifty,
    PageSizeOption.SeventyFive,
    PageSizeOption.OneHundred,
  ].map(value => ({
    name: pageSizeMapper(value, userLanguage),
    value: value,
  }));

  return (
    <div className="mt-1 mb-6 rounded-2xl border border-gray-200 bg-white px-4 py-4 sm:px-5 sm:py-4">
      <button
        type="button"
        onClick={() => setIsCollapsed((prev) => !prev)}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span className="text-sm font-semibold uppercase tracking-wide text-gray-700">
          {texts.header}
        </span>
        <span
          className={`inline-flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 text-xs text-gray-600 transition-transform duration-150 ${
            isCollapsed ? '' : 'rotate-180'
          }`}
        >
          ▼
        </span>
      </button>

      <div
        className={`grid grid-cols-12 gap-4 pt-4 transition-all duration-200 ${
          isCollapsed ? 'pointer-events-none max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'
        }`}
      >
        <div className="col-span-12 md:col-span-4">
          <NormalFormTextField
            label={texts.search}
            name="search"
            placeholder={texts.searchPlaceholder}
            value={filters.search || ''}
            onChange={(value) => handleFieldChange('search', value)}
          />
        </div>

        <div className="col-span-12 md:col-span-2">
          <div className="mb-3">
            <label
              htmlFor="countryDropdown"
              className="mb-2 block text-[15px] font-semibold text-gray-800"
            >
              {userLanguage === 'es' ? 'País' : 'Country'}
            </label>
            <div className="relative" ref={countryRef}>
              <button
                id="countryDropdown"
                type="button"
                onClick={() => {
                  setIsCountryOpen((prev) => !prev);
                  setIsStateOpen(false);
                }}
                aria-haspopup="listbox"
                aria-expanded={isCountryOpen}
                className="flex w-full items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-800 transition hover:bg-gray-100"
              >
                <span className="block w-full overflow-hidden text-ellipsis whitespace-nowrap text-left">
                  {countrySummary}
                </span>
                <span
                  className={`ml-1 inline-block h-2 w-2 rotate-45 border-b-2 border-r-2 border-gray-700 transition-transform duration-150 ${
                    isCountryOpen ? '-rotate-135' : ''
                  }`}
                />
              </button>
              {isCountryOpen && (
                <div
                  className="absolute left-0 right-0 top-[calc(100%+8px)] z-20 max-h-56 overflow-auto rounded-md bg-gray-50 p-3 shadow-lg"
                  role="listbox"
                  aria-label={userLanguage === 'es' ? 'País' : 'Country'}
                >
                  <div className="grid grid-cols-1 gap-2">
                    {countryOptions.map((opt) => {
                      const checked = selectedCountryIds.includes(opt.value);
                      return (
                        <label
                          key={opt.value}
                          className="flex cursor-pointer items-center gap-2 text-[13px] font-medium text-gray-800"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleCountryToggle(opt.value)}
                          />
                          <span>{opt.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-2">
          <div className="mb-3">
            <label
              htmlFor="stateDropdown"
              className="mb-2 block text-[15px] font-semibold text-gray-800"
            >
              {userLanguage === 'es' ? 'Estado / Provincia' : 'State / Province'}
            </label>
            <div className="relative" ref={stateRef}>
              <button
                id="stateDropdown"
                type="button"
                onClick={() => {
                  if (!activeCountryId) return;
                  setIsStateOpen((prev) => !prev);
                  setIsCountryOpen(false);
                }}
                aria-haspopup="listbox"
                aria-expanded={isStateOpen}
                className="flex w-full items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-800 transition hover:bg-gray-100"
              >
                <span className="block w-full overflow-hidden text-ellipsis whitespace-nowrap text-left">
                  {activeCountryId
                    ? stateSummary
                    : userLanguage === 'es'
                      ? 'Elegí 1 país'
                      : 'Select 1 country'}
                </span>
                <span
                  className={`ml-1 inline-block h-2 w-2 rotate-45 border-b-2 border-r-2 border-gray-700 transition-transform duration-150 ${
                    isStateOpen ? '-rotate-135' : ''
                  }`}
                />
              </button>
              {!activeCountryId && (
                <div className="pointer-events-none absolute inset-0 rounded-md bg-white/60" />
              )}
              {isStateOpen && activeCountryId && (
                <div
                  className="absolute left-0 right-0 top-[calc(100%+8px)] z-20 max-h-56 overflow-auto rounded-md bg-gray-50 p-3 shadow-lg"
                  role="listbox"
                  aria-label={userLanguage === 'es' ? 'Estado / Provincia' : 'State / Province'}
                >
                  <div className="grid grid-cols-1 gap-2">
                    {stateOptions.map((opt) => {
                      const checked = selectedStateIds.includes(opt.value);
                      return (
                        <label
                          key={opt.value}
                          className="flex cursor-pointer items-center gap-2 text-[13px] font-medium text-gray-800"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleStateToggle(opt.value)}
                          />
                          <span>{opt.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-2">
          <NormalFormSelectionField<PriorityOption>
            label={texts.field1}
            name="priority"
            options={priorityOption}
            value={filters.priorityOption}
            onChange={(value) => handleFieldChange('priorityOption', value)}
            noValueText={userLanguage === 'es' ? 'Todos' : 'All'}
          />
        </div>

        <div className="col-span-12 md:col-span-2">
          <div className="mb-3">
            <label
              htmlFor="includeStatusDropdown"
              className="mb-2 block text-[15px] font-semibold text-gray-800"
            >
              {texts.field2}
            </label>
            <div className="relative" ref={includeRef}>
              <button
                id="includeStatusDropdown"
                type="button"
                onClick={() => {
                  setIsIncludeOpen((prev) => !prev);
                  setIsExcludeOpen(false);
                  setIsCountryOpen(false);
                  setIsStateOpen(false);
                }}
                aria-haspopup="listbox"
                aria-expanded={isIncludeOpen}
                className="flex w-full items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-800 transition hover:bg-gray-100"
              >
                <span className="block w-full overflow-hidden text-ellipsis whitespace-nowrap text-left">
                  {includeSummary}
                </span>
                <span
                  className={`ml-1 inline-block h-2 w-2 rotate-45 border-b-2 border-r-2 border-gray-700 transition-transform duration-150 ${
                    isIncludeOpen ? '-rotate-135' : ''
                  }`}
                />
              </button>
              {isIncludeOpen && (
                <div
                  className="absolute left-0 right-0 top-[calc(100%+8px)] z-20 max-h-56 overflow-auto rounded-md bg-gray-50 p-3 shadow-lg"
                  role="listbox"
                  aria-label={texts.field2}
                >
                  <div className="grid grid-cols-1 gap-2">
                    {statusOptions.map((opt) => {
                      const checked = selectedIncludeStatuses.includes(opt.value);
                      return (
                        <label
                          key={opt.value}
                          className="flex cursor-pointer items-center gap-2 text-[13px] font-medium text-gray-800"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleIncludeStatusToggle(opt.value)}
                          />
                          <span>{opt.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-2">
          <div className="mb-3">
            <label
              htmlFor="excludeStatusDropdown"
              className="mb-2 block text-[15px] font-semibold text-gray-800"
            >
              {texts.field3}
            </label>
            <div className="relative" ref={excludeRef}>
              <button
                id="excludeStatusDropdown"
                type="button"
                onClick={() => {
                  setIsExcludeOpen((prev) => !prev);
                  setIsIncludeOpen(false);
                  setIsCountryOpen(false);
                  setIsStateOpen(false);
                }}
                aria-haspopup="listbox"
                aria-expanded={isExcludeOpen}
                className="flex w-full items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-800 transition hover:bg-gray-100"
              >
                <span className="block w-full overflow-hidden text-ellipsis whitespace-nowrap text-left">
                  {excludeSummary}
                </span>
                <span
                  className={`ml-1 inline-block h-2 w-2 rotate-45 border-b-2 border-r-2 border-gray-700 transition-transform duration-150 ${
                    isExcludeOpen ? '-rotate-135' : ''
                  }`}
                />
              </button>
              {isExcludeOpen && (
                <div
                  className="absolute left-0 right-0 top-[calc(100%+8px)] z-20 max-h-56 overflow-auto rounded-md bg-gray-50 p-3 shadow-lg"
                  role="listbox"
                  aria-label={texts.field3}
                >
                  <div className="grid grid-cols-1 gap-2">
                    {statusOptions.map((opt) => {
                      const checked = selectedExcludeStatuses.includes(opt.value);
                      return (
                        <label
                          key={opt.value}
                          className="flex cursor-pointer items-center gap-2 text-[13px] font-medium text-gray-800"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleExcludeStatusToggle(opt.value)}
                          />
                          <span>{opt.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-2">
          <NormalFormSelectionField<IncidentTypeOption>
            label={texts.field4}
            name="type"
            options={typeOptions}
            value={filters.typeOption}
            onChange={(value) => handleFieldChange('typeOption', value)}
            noValueText={userLanguage === 'es' ? 'Todos' : 'All'}
          />
        </div>

        <div className="col-span-12 md:col-span-3">
          <NormalFormSelectionField<IncidentCategoryOption>
            label={texts.field5}
            name="category"
            options={categoryOptions}
            value={filters.categoryOption}
            onChange={(value) => handleFieldChange('categoryOption', value)}
            noValueText="Todas"
          />
        </div>

        <div className="col-span-12 md:col-span-3">
          <NormalFormSelectionField<OrderDirectionOption>
            label={texts.field6}
            name="orderDirection"
            options={orderDirectionOptions}
            value={filters.orderDirection}
            onChange={(value) => handleFieldChange('orderDirection', value)}
          />
        </div>

        <div className="col-span-12 md:col-span-3">
          <NormalFormSelectionField<PageSizeOption>
            label={texts.field7}
            name="pageSize"
            options={pageSizeOptions}
            value={filters.pageSize}
            onChange={(value) => handleFieldChange('pageSize', value)}
            noValueText={userLanguage === 'es' ? 'Todos' : 'All'}
          />
        </div>

        <div className="col-span-12 md:col-span-3 flex items-end justify-end">
          <Button secondary onClick={() => onFilterChange(initialFilters)}>
            Limpiar Filtros
          </Button>
        </div>
      </div>
    </div>
  );
};